import styled from "@emotion/styled/macro";
import { useCallback, useMemo } from "react";
import {
  Pill,
  Typography,
  SvgNames,
  SvgIcon,
  Skeleton,
  Tooltip,
  tokens,
  Button,
} from "@sunrun/experience-ui-components";

type StatusType = "success" | "warning";

type Body = { type: "success" | "warning" | "problem" | "info"; text: string };

type ConfirmInfoOptionalTaskProps = {
  title: string;
  description?: string;
  status?: {
    type: StatusType;
    text: string;
  };
  showInfoIcon?: boolean;
  onLaunch?: () => void;
  onLaunchText?: string;
  children?: React.ReactNode;
  body?: Body | (Body | undefined)[];
  additionalText?: string;
  testId?: string;
  loading?: boolean;
  tooltip?: string;
  additionalTextPosition?: "bottom" | "top";
  link?: string;
  linkText?: string;
};

const StyledSkeleton = styled(Skeleton)`
  border: 1px solid #e2e2e2;
  border-radius: 16px;
  background-color: #fffbf0;
  max-width: 260px;
  display: flex;
  height: 174px;
`;

const Card = styled.div`
  border: 1px solid #e2e2e2;
  border-radius: 16px;
  background-color: #fffbf0;
  padding: 20px;
  min-width: 260px;
  max-width: 260px;
  min-height: 130px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  font-family: "Inter", sans-serif;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const Description = styled.div`
  font-size: 14px;
  color: #555;
  margin: 0;
  margin-top: 12px;
  line-height: 1.4;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: space-between;
`;

const LaunchButton = styled.button`
  margin-top: auto;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  border: 1px solid #1a1a1a;
  border-radius: 9999px;
  background-color: transparent;
  cursor: pointer;
  max-width: 100%;
  width: 100px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ConfirmInfoOptionalTask = ({
  title,
  onLaunch,
  onLaunchText,
  body,
  additionalText,
  testId,
  loading = false,
  tooltip,
  additionalTextPosition = "bottom",
  link,
  linkText,
}: ConfirmInfoOptionalTaskProps) => {
  testId = testId || title.toLowerCase().replace(/\s+/g, "-") + "-button";

  const getBodyContent = useCallback((body: Body | undefined) => {
    if (!body) return null;

    switch (body.type) {
      case "success":
        return <StyledPill text={body.text} type="success" />;
      case "warning":
        return <StyledPill text={body.text} type="warning" />;
      case "problem":
        return (
          <StyledPill text={body.text} type="warning" icon={SvgNames.Warning} />
        );
      case "info":
        return body.text;
      default:
        return null;
    }
  }, []);

  const taskContent = useMemo(() => {
    if (Array.isArray(body)) {
      return body.map((item) => {
        return getBodyContent(item);
      });
    }

    return getBodyContent(body);
  }, [body, getBodyContent]);

  if (loading) {
    return <StyledSkeleton />;
  }
  return (
    <Card>
      <div>
        <TitleRow>
          <Title>{title}</Title>
          {tooltip && (
            <Tooltip content={tooltip} className="icon-tooltip" direction="top">
              <SvgIcon
                name={SvgNames.InfoOutline}
                width={20}
                height={20}
                color={tokens.HEROBLUE_50}
              />
            </Tooltip>
          )}
        </TitleRow>
        {additionalText && additionalTextPosition === "top" && (
          <Typography>{additionalText}</Typography>
        )}
        {!loading && <Description>{taskContent}</Description>}
      </div>
      {link && linkText && (
        <StyledLinkButton
          color="link"
          onClick={() => window.open(link, "_blank")}
        >
          {linkText}
        </StyledLinkButton>
      )}
      {onLaunch && (
        <LaunchButton data-testid={testId} onClick={onLaunch}>
          {onLaunchText || "Launch"}
        </LaunchButton>
      )}
      {additionalText && <Typography>{additionalText}</Typography>}
    </Card>
  );
};

const StyledPill = styled(Pill)`
  max-width: 100%;
  width: 100% !important;
  white-space: wrap !important;
  padding: 7px 24px !important;
  > p {
    line-height: 16px;
    text-align: left;
    padding-left: 8px;
  }
`;

const StyledLinkButton = styled(Button)`
  align-self: start;
`;

export { ConfirmInfoOptionalTask };
